import { useQuery } from '@apollo/client/react';
import { Box, Paper, Typography, Stack, Skeleton } from '@mui/material';
import {
  ssrApolloService,
  useRouteInfo,
  useTranslation,
  Types,
} from '@scorenco/core';
import {
  NextAdArea,
  NextAkceloConfig,
  NextGooglePublisherTagConfig,
  NextPageWithLayout,
} from '@scorenco/next';

import { PageLayout, PageSeo, SportChip } from '@/components';
import { WebLayout } from '@/layouts';
import { RouteKey } from '@/routes';
import { getSport, extractBase36Id } from '@/utils';

import { CompetitionList } from './_internal/CompetitionList';
import {
  GetLeagueInfosDocument,
  GetLevelInSeasonByLeagueIdDocument,
} from './_internal/League.query.generated';

const extractUrlInfo = (query: Record<string, string | undefined>) => {
  const leagueId = extractBase36Id(query.league);

  return { leagueId };
};

const League: NextPageWithLayout<{ sport: Types.Clubs_Sport }> = ({
  sport,
}) => {
  const routeInfo = useRouteInfo();
  const { t } = useTranslation();
  const urlInfo = extractUrlInfo(routeInfo.query);

  const sportSlug = routeInfo.query.sport;

  const leagueQuery = useQuery(GetLeagueInfosDocument, {
    variables: { id: urlInfo.leagueId as number },
    skip: !urlInfo.leagueId,
  });

  const levelsInSeasonQuery = useQuery(GetLevelInSeasonByLeagueIdDocument, {
    variables: { leagueId: urlInfo.leagueId as number },
    skip: !urlInfo.leagueId,
  });

  const league = leagueQuery.data?.competitions_league_by_pk;
  const levelsByCategory =
    levelsInSeasonQuery.data?.competitions_level_for_season_detail_by_league_id?.reduce<
      Record<any, Types.Competitions_Level_For_Season_Detail[]>
    >((group, level) => {
      const categoryKey =
        level?.level?.age_category + ' ' + level?.level?.gender || 'unknown';
      group[categoryKey] = group[categoryKey] ?? [];
      group[categoryKey].push(level);
      return group;
    }, {}) || { unknown: undefined };

  return (
    <Box>
      <PageSeo
        title={`${league?.name} - Score'n'co`}
        description={`${league?.name} sur Score'n'co 🔵🟢`}
        url={{
          routeKey: RouteKey.Sport,
          params: {
            sport: sportSlug,
            league: league?.slug + '-' + league?.id.toString(36),
          },
        }}
      />

      <NextAkceloConfig />
      <NextGooglePublisherTagConfig />

      <PageLayout
        image={sport?.image}
        breadcrumbs={[
          {
            label: sport?.name || '',
            href: {
              routeKey: RouteKey.Competitions,
              params: { sport: sportSlug },
            },
          },
          {
            label: league?.name || '',
          },
        ]}
      >
        <Paper variant="bordered">
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={4}
            width="100%"
            alignItems="center"
          >
            <Box
              sx={{
                '@media screen and (max-width: 450px)': {
                  margin: '0 !important',
                  textAlign: 'center',
                },
              }}
            >
              <SportChip
                sport={sport?.slug}
                sx={{
                  mb: 2,
                }}
              />

              <Typography variant="h1" component="h1">
                {league?.name}
              </Typography>
              <Typography variant="h3" component="h1">
                {league?.area ? (
                  t(`COMPETITION_AREA_${league?.area}`)
                ) : (
                  <Skeleton width={200} />
                )}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <NextAdArea
          akceloSlotId="Scorenco_PlayRVideo"
          akceloAdUnitClass="akcelo-adunit-15786"
          withGooglePublisherTag
        />

        <Box sx={{ marginTop: 3 }}>
          {levelsByCategory &&
            Object.entries(levelsByCategory).map(([category, levels]) => {
              return (
                <CompetitionList
                  key={category}
                  sx={{ marginTop: 3 }}
                  category={category}
                  levels={levels}
                />
              );
            })}
        </Box>
      </PageLayout>
    </Box>
  );
};

export const getServerSideProps = WebLayout.buildGetServerSideProps({}, [
  async ({ query }, previous) => {
    const { apolloClient, addApolloState } = ssrApolloService();

    const sportSlug = query.sport;
    const urlInfo = extractUrlInfo(
      query as any as { sport: string; league: string }
    );

    const leagueId = urlInfo?.leagueId;

    if (!leagueId || typeof sportSlug !== 'string') {
      return { notFound: true };
    }

    if (query['no-ssr'] === undefined) {
      const sport = await getSport(sportSlug);
      const leagueQuery = await apolloClient.query({
        query: GetLeagueInfosDocument,
        variables: { id: urlInfo.leagueId as number },
      });

      const league = leagueQuery.data?.competitions_league_by_pk;

      if (!league) {
        return { notFound: true };
      }

      // if (isConnected(req) && league?.old_url) {
      //   return {
      //     redirect: {
      //       statusCode: 302,
      //       destination: process.env.NEXT_PUBLIC_OLD_WEB_URL + league?.old_url,
      //     },
      //   };
      // }

      // Chargement des données pour le rendu côté serveur
      await apolloClient.query({
        query: GetLevelInSeasonByLeagueIdDocument,
        variables: { leagueId: urlInfo.leagueId as number },
      });

      return addApolloState({
        ...previous,
        props: {
          ...previous.props,
          sport,
        },
      });
    }

    return addApolloState(previous);
  },
]);

League.getLayout = WebLayout.buildGetLayout({
  strapiMenu: true,
  amplify: { protectedPage: false },
});

export default League;
